import {Component, EventEmitter, Input, OnChanges, Output, SimpleChanges, ViewChild} from '@angular/core';
import {BaseComponent} from "../../../../BaseComponent";
import {CompareActionEnum} from "../../../../../models/device/DeviceTemplateCompare";
import {ProtocolTypeEnum} from "../../../../../enums/registry/ProtocolTypeEnum";
import {getRowKey} from "../../../../../utils/rowKey";
import {DxDataGridComponent} from "devextreme-angular";
import {RowStateTracker} from "../../../../../utils/RowStateTracker";
import * as uuid from "uuid";

// `groups`/outputs are typed loosely (any) since this component is shared between the
// compare popup (CompareGroupVM rows) and the device drawer (MetricGroupInstanceDraft
// rows) - those two models don't share a strict-template-compatible shape (e.g. `id` is
// required on one, optional on the other), same tradeoff the rest of this grid-heavy
// subsystem already makes (see BaseProtocolFormComponent.groupMetric: any).
@Component({
  selector: 'app-measure-group-table',
  templateUrl: './measure-group-table.component.html',
  styleUrls: ['./measure-group-table.component.css']
})
export class MeasureGroupTableComponent extends BaseComponent implements OnChanges {

  @ViewChild('groupDataGrid', {static: false}) dataGrid?: DxDataGridComponent;

  readonly rowStateTracker = new RowStateTracker();

  @Input() groups: any[] = [];
  @Input() protocolType?: ProtocolTypeEnum;
  @Input() selectedGroup?: any;
  @Input() showAction: boolean = false;
  @Input() showActiveColumn: boolean = true;
  @Input() showDelete: boolean = false;
  @Input() editable: boolean = false;
  @Input() showToggleAllCheckbox: boolean = true;
  @Input() showAddButton: boolean = true;
  @Input() showDeleteRowButton: boolean = true;
  @Input() functionColumnReadOnly: boolean = false;
  @Input() emptyMessageKey!: string;
  @Input() titleSuffix?: string;
  // The metric table's tracker, scoped by group key: drives each group row's dot and blocks
  // switching group while a metric is being edited.
  @Input() metricRowStateTracker?: RowStateTracker;

  @Output() groupClick = new EventEmitter<any>();
  @Output() addGroup = new EventEmitter<any>();
  @Output() addAllGroups = new EventEmitter<any[]>();
  @Output() deleteGroup = new EventEmitter<any>();
  @Output() groupsChanged = new EventEmitter<void>();
  @Output() metricEditBlocked = new EventEmitter<void>();

  protected readonly CompareActionEnum = CompareActionEnum;
  protected readonly ProtocolTypeEnum = ProtocolTypeEnum;
  protected readonly getRowKey = getRowKey;

  protected rowIsBeingEdited: boolean = false;

  get hasAddableGroups(): boolean {
    return this.groups.some(g => g.action === CompareActionEnum.ADD);
  }

  // Tri-state: true only if every group is fully enabled, false only if every group is
  // fully disabled (or there are no groups), null (indeterminate) whenever groups
  // disagree or any group itself has a mix of selected/unselected metrics.
  get allGroupsEnabled(): boolean | null {
    if (this.groups.length === 0) return false;
    const states = this.groups.map(g => this.getGroupEnabledState(g));
    if (states.every(s => s === true)) return true;
    if (states.every(s => s === false)) return false;
    return null;
  }

  // Derived live from the group's own metrics - never cached onto the group, so it can't
  // go stale relative to edits made in the metric table.
  getGroupEnabledState(group: any): boolean | null {
    const metrics = group.metrics ?? [];
    if (metrics.length === 0) return false;
    if (metrics.every((m: any) => m.selected !== false)) return true;
    if (metrics.every((m: any) => m.selected === false)) return false;
    return null;
  }

  onGroupEnabledChanged(group: any, value: boolean) {
    this.setGroupEnabled(group, value);
    this.groupsChanged.emit();
  }

  onToggleAllGroupsEnabled(value: boolean) {
    this.groups.forEach(g => this.setGroupEnabled(g, value));
    this.groupsChanged.emit();
  }

  // Only the metrics count as modified, not the group row itself: the group just shows the dot.
  private setGroupEnabled(group: any, value: boolean) {
    const groupKey = getRowKey(group);
    group.metrics?.forEach((m: any) => {
      if ((m.selected !== false) === value) return;
      m.selected = value;
      this.metricRowStateTracker?.markModified(m._uiKey, groupKey);
    });
  }

  getAvailablePollingFunctions(): any[] {
    switch (this.protocolType) {
      case ProtocolTypeEnum.MODBUS_RTU:
      case ProtocolTypeEnum.MODBUS_TCP:
        return this.gmService.modbusFunctionValues;
      case ProtocolTypeEnum.SIEMENS_S7:
        return this.gmService.s7FunctionValues;
      default:
        return [];
    }
  }

  // Group instances don't carry a dedicated isWritableOnEdge flag (only the device type's
  // template MetricGroup does) - approximated from the group's own polling function instead,
  // since write-capable functions are the only ones labeled "Write" in the function list.
  getGroupWriteable = (rowData: any): boolean => {
    const fn = this.getAvailablePollingFunctions().find(f => f.value === rowData?.transportConfiguration?.function);
    return !!fn?.label && /write/i.test(fn.label);
  };

  ngOnChanges(changes: SimpleChanges) {
    if (changes['selectedGroup'] && this.selectedGroup)
      this.dataGrid?.instance.navigateToRow(getRowKey(this.selectedGroup));
  }

  // Switching group swaps the metric grid's rows, which would drop the metric being edited.
  onRowClick($event: any) {
    if (this.metricRowStateTracker?.hasEditing()) {
      if (getRowKey($event.data) !== getRowKey(this.selectedGroup))
        this.metricEditBlocked.emit();
      return;
    }
    if (!this.rowIsBeingEdited)
      this.groupClick.emit($event.data);
  }

  onAddClick($event: MouseEvent, group: any) {
    $event.stopPropagation();
    this.addGroup.emit(group);
  }

  onAddAllClick() {
    this.addAllGroups.emit(this.groups.filter(g => g.action === CompareActionEnum.ADD));
  }

  onDeleteClick($event: MouseEvent, group: any) {
    $event.stopPropagation();
    this.deleteGroup.emit(group);
  }

  // Called by the parent before a device save, if a row is still "in modifica" here.
  cancelActiveEdit(): void {
    this.dataGrid?.instance.cancelEditData();
  }

  async onEditingStart($event: any) {
    if (this.rowIsBeingEdited) {
      $event.cancel = true;
      const discard = await this.showDiscardEditsDialogWithPromise();
      if (discard) {
        this.rowIsBeingEdited = false;
        $event.component.editRow($event.component.getRowIndexByKey(getRowKey($event.data)));
      }
      return;
    }
    this.rowIsBeingEdited = true;
  }

  onEditCanceled() {
    this.rowIsBeingEdited = false;
  }

  onRowSaved() {
    this.rowIsBeingEdited = false;
    this.groupsChanged.emit();
  }

  onRowRemoved($event: any) {
    this.metricRowStateTracker?.resetScope(getRowKey($event.data));
    this.groupsChanged.emit();
  }

  onCellPreparedSwapSaveIcon(e: any) {
    if (e.rowType === 'data' && e.column.command === 'edit') {
      const saveButton = e.cellElement.querySelector('.dx-link-save');
      if (saveButton) {
        saveButton.classList.remove('dx-icon-save');
        saveButton.classList.add('dx-icon-check');
      }
    }
  }

  // A device-only new row has no template group to link to (metricGroupId stays null) -
  // gets a client-side _uiKey immediately since keyExpr requires one to be defined.
  onInitNewRow($event: any) {
    $event.data.metricOffset = 0;
    $event.data.metrics = [];
    $event.data._uiKey = uuid.v4();
  }

  editGroupAddRowButton($event: any) {
    const toolbarItems = $event.toolbarOptions.items;
    toolbarItems.forEach((item: any) => {
      if (item.name === 'addRowButton') {
        item.options = {
          icon: 'add',
          onClick: () => this.customGroupAddRow($event)
        };
      }
    });
  }

  customGroupAddRow($event: any) {
    if (!this.rowIsBeingEdited) {
      $event.component.addRow().done(() => {
        this.rowIsBeingEdited = true;
      });
    } else {
      this.showDiscardEditsDialog().show().done((dialogResult: any) => {
        if (dialogResult) {
          $event.component.addRow().done(() => {
            this.rowIsBeingEdited = true;
          });
        }
      });
    }
  }
}
